import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import logger from "@/lib/logger";

type PublicTierStatus = { canJoin: boolean; messagesPerDay: number };
type JoinOutcome = "granted" | "already_entitled" | "capacity_full";

/**
 * Shown to authenticated-but-unentitled users (spec §11.1). They are NOT
 * rejected by the kernel — they may still be members of someone else's
 * campaign — but self-serve access needs either an invite or, when public
 * capacity exists, self-serve joining the free tier.
 */
export default function InviteRequiredPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<PublicTierStatus>({
    canJoin: false,
    messagesPerDay: 0,
  });
  const [loading, setLoading] = useState(true);
  const [capacityFull, setCapacityFull] = useState(false);
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    let mounted = true;
    fetch("/api/entitlements/public-tier")
      .then((res) => {
        if (!res.ok) {
          throw new Error(`public-tier responded ${res.status}`);
        }
        return res.json();
      })
      .then((body: PublicTierStatus) => {
        if (mounted) {
          setStatus(body);
        }
      })
      .catch((error: unknown) => {
        // Fail-secure: any error → treat as canJoin false (the default state).
        logger.error({ err: error }, "[Join] public-tier fetch failed");
      })
      .finally(() => {
        if (mounted) {
          setLoading(false);
        }
      });
    return () => {
      mounted = false;
    };
  }, []);

  const handleJoin = async () => {
    setJoining(true);
    try {
      const res = await fetch("/api/entitlements/join", { method: "POST" });
      if (!res.ok) {
        throw new Error(`join responded ${res.status}`);
      }
      const body: { outcome: JoinOutcome } = await res.json();
      if (body.outcome === "granted" || body.outcome === "already_entitled") {
        // Hard-navigate: forces a fresh useEntitlement probe. A soft
        // navigate would keep the cached null unentitled state (spec §11.1).
        window.location.assign("/");
        return;
      }
      // capacity_full: a race lost the last slot between the status read
      // and the join.
      setCapacityFull(true);
    } catch (error: unknown) {
      // A request failure (non-ok status, network error, or bad JSON) is
      // NOT the same as capacity_full — leave the page joinable so the
      // user can retry, rather than mislabeling a server error.
      logger.error({ err: error }, "[Join] join request failed");
    } finally {
      setJoining(false);
    }
  };

  const canJoinNow = !loading && status.canJoin && !capacityFull;

  return (
    <div className="flex h-dvh w-screen items-start justify-center bg-background pt-12 md:items-center md:pt-0">
      <div className="flex w-full max-w-md flex-col gap-8 rounded-2xl border border-zinc-200 p-8 text-center dark:border-zinc-800">
        <div className="flex flex-col gap-2">
          <h3 className="font-semibold text-xl">
            {canJoinNow ? "Join Verderem" : "Invite required"}
          </h3>
          <p className="text-muted-foreground text-sm">
            {canJoinNow
              ? "Have an invite instead? Ask Barry for an invite link."
              : "Verderem is in private alpha. Ask Barry for an invite link."}
          </p>
        </div>
        {canJoinNow && (
          <Button
            className="w-full"
            disabled={joining}
            onClick={handleJoin}
            type="button"
          >
            {`Join the free tier — ${status.messagesPerDay} messages/day`}
          </Button>
        )}
        <Button
          className="w-full"
          onClick={() => navigate("/logout")}
          type="button"
          variant="outline"
        >
          Sign out
        </Button>
      </div>
    </div>
  );
}
